// Angular
import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
// Components
import {BaseComponent} from './views/theme/base/base.component';
import {ErrorPageComponent} from './views/theme/content/error-page/error-page.component';
// Auth
// import {AuthGuard} from './core/auth';
import { MachineListComponent } from './views/pages/machine-list/machine-list.component';
import { RegisterMachineComponent } from './views/pages/register-machine/register-machine.component';
import { AuthGuardService } from './core/_guard/auth-guard.service';
import { RoleGuardService } from './core/_guard/role-guard.service';
import { FeatureFlagGuardService } from './core/_guard/feature-flag-guard.service';
import { PortalViewGuardService } from './core/_guard/portal-view-guard.service';
import { BulkActionsComponent } from './views/pages/bulk-actions/bulk-actions.component';
import { SubsConfirmationComponent } from './views/pages/subs-confirmation/subs-confirmation.component';
import { AnalyticsComponent } from './views/pages/analytics/analytics.component';
import { MachineResolver } from './views/pages/machine/machine.resolver';

const routes: Routes = [
	{path: 'confirm', component: SubsConfirmationComponent},
	{path: '', loadChildren: () => import('app/views/pages/auth/auth.module').then(m => m.AuthModule)},
	{
		path: '',
		component: BaseComponent,
		canActivateChild: [PortalViewGuardService],
		children: [
			{
				path: 'dashboard',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/new-dashboard/new-dashboard.module').then(m => m.NewDashboardModule),
			},
			{
				path: 'user-settings',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/user-settings/user-settings.module').then(m => m.UserSettingsModule),
			},
			{
				path: 'card-readers',
				canActivate: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin'], featureFlag: 'card_reader_list'},
				loadChildren: () => import('app/views/pages/card-readers/card-readers.module').then(m => m.CardReadersModule),
			},
			{
				path: 'retail-analytics',
				canActivate: [AuthGuardService, RoleGuardService],
				data: {roles: ['sadmin']},
				loadChildren: () => import('app/views/pages/retail-analytics/retail-analytics.module').then(m => m.RetailAnalyticsModule),
			},
			{
				path: 'cold-brew',
				canActivate: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin'], featureFlag: 'cold_brew'},
				loadChildren: () => import('app/views/pages/cold-brew/cold-brew.module').then(m => m.ColdBrewModule),
			},
			{
				path: 'machine-list',
				canActivateChild: [AuthGuardService],
				component: MachineListComponent,
			},
			{
				path: 'banners',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/banners/banners.module').then(m => m.BannersModule),
			},
			{
				path: 'machine/:machineId',
				canActivateChild: [AuthGuardService],
				resolve: { machine: MachineResolver },
				loadChildren: () => import('app/views/pages/machine/machine.module').then(m => m.MachineModule),
			},
			{
				path: 'clients',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/clients/clients.module').then(m => m.ClientsModule),
			},
			{
				path: 'bulk-update',
				canActivateChild: [AuthGuardService],
				component: BulkActionsComponent,
			},
			{
				path: 'users',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/users/users.module').then(m => m.UsersModule),
			},
			{
				path: 'package',
				canActivateChild: [AuthGuardService, RoleGuardService],
				data: {roles: ['sadmin']},
				loadChildren: () => import('app/views/pages/packages/packages.module').then(m => m.PackagesModule)
			},
			{
				path: 'audits',
				canActivateChild: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin'], featureFlag: 'camera_audit'},
				loadChildren: () => import('app/views/pages/camera-audit-live/camera-audit.module').then(m => m.CameraAuditModule)
			},
			{
				path: 'analytics',
				canActivateChild: [AuthGuardService],
				component: AnalyticsComponent,
			},
			{
				path: 'inventory',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/inventory/inventory.module').then(m => m.InventoryModule),
			},
			{
				path: 'new-dashboard',
				canActivateChild: [AuthGuardService],
				loadChildren: () => import('app/views/pages/new-dashboard/new-dashboard.module').then(m => m.NewDashboardModule),
			},
			{
				path: 'register-machine',
				canActivate: [AuthGuardService, RoleGuardService],
				data: {roles: ['sadmin']},
				component: RegisterMachineComponent,
			},
			{
				path: 'orders',
				canActivateChild: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin', 'distiadmin', 'marketadmin', 'clientadmin', 'campusadmin'], featureFlag: 'orders'},
				loadChildren: () => import('app/views/pages/orders/orders.module').then(m => m.OrdersModule),
			},
			{
				path: 'voucher',
				canActivateChild: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin', 'campusadmin'], featureFlag: 'vouchers'},
				loadChildren: () => import('app/views/pages/voucher/voucher.module').then(m => m.VoucherModule),
			},
			{
				path: 'voucher-ops',
				canActivateChild: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin'], featureFlag: 'vouchers'},
				loadChildren: () => import('app/views/pages/voucher/voucher-ops.module').then(m => m.VoucherOpsModule),
			},
			{
				path: 'flex',
				canActivateChild: [AuthGuardService, RoleGuardService, FeatureFlagGuardService],
				data: {roles: ['sadmin'], featureFlag: 'flex_mock'},
				loadChildren: () => import('app/views/pages/subsidies/subsidies.module').then(m => m.SubsidiesModule),
			},
			{
				path: 'error/403',
				component: ErrorPageComponent,
				data: {
					type: 'error-v6',
					code: 403,
					title: '403... Access forbidden',
					desc: 'Looks like you don\'t have permission to access for requested page.<br> Please, contact administrator',
				},
			},
			{path: 'error/:type', component: ErrorPageComponent},
			{path: '', redirectTo: 'dashboard', pathMatch: 'full'},
			{path: '**', redirectTo: 'dashboard', pathMatch: 'full'},
		],
	},

	{path: '**', redirectTo: 'error/403', pathMatch: 'full'},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {
}
